import React, { useRef, useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import obtenerImagen from '../../services/archivosService';
import './signature-pad.css'

const CANVAS_WIDTH = 600;
const CANVAS_HEIGHT = 200;

function SignaturePad({ label, value, onChange, error }) {
  const canvasRef = useRef(null);
  const dibujando = useRef(false);
  const [tieneTrazo, setTieneTrazo] = useState(Boolean(value));
  const [firmaGuardadaVisible, setFirmaGuardadaVisible] = useState(Boolean(value));

  const limpiarCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  const obtenerPos = (evt) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = evt.touches ? evt.touches[0].clientX : evt.clientX;
    const clientY = evt.touches ? evt.touches[0].clientY : evt.clientY;
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return { x: (clientX - rect.left) * scaleX, y: (clientY - rect.top) * scaleY };
  };

  const iniciarTrazo = (e) => {
    e.preventDefault();
    dibujando.current = true;
    setFirmaGuardadaVisible(false); // el usuario empezó a reemplazarla
    const ctx = canvasRef.current.getContext('2d');
    const pos = obtenerPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const continuarTrazo = (e) => {
    if (!dibujando.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = obtenerPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#1a1a1a';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    setTieneTrazo(true);
  };

  const terminarTrazo = () => {
    if (!dibujando.current) return;
    dibujando.current = false;
    const dataUrl = canvasRef.current.toDataURL('image/png');
    onChange?.(dataUrl);
  };

  const limpiar = () => {
    limpiarCanvas();
    setTieneTrazo(false);
    setFirmaGuardadaVisible(false);
    onChange?.(null);
  };

  // Cargar firma guardada (si existe)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let blobUrlCreado = null;
    let cancelado = false;

    const dibujar = (src) => {
      const img = new Image();
      img.onload = () => {
        if (cancelado) return;
        const ctx = canvas.getContext('2d');

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        setTieneTrazo(true);
        setFirmaGuardadaVisible(true);
      };

      img.onerror = () => {
        console.error('No se pudo cargar la firma guardada');
        limpiarCanvas();
        setTieneTrazo(false);
        setFirmaGuardadaVisible(false);
      };

      img.src = src;
    };

    (async () => {
      if (!value) {
        limpiarCanvas();
        setTieneTrazo(false);
        setFirmaGuardadaVisible(false);
        return;
      }

      if (value.startsWith('data:')) {
        // firma recién dibujada en este mismo formulario — no necesita auth
        dibujar(value);
        return;
      }

      try {
        const blobUrl = await obtenerImagen(value);
        if (cancelado) {
          URL.revokeObjectURL(blobUrl);
          return;
        }
        blobUrlCreado = blobUrl;
        dibujar(blobUrl);
      } catch (err) {
        console.error('No se pudo cargar la firma guardada:', err);
        limpiarCanvas();
        setTieneTrazo(false);
        setFirmaGuardadaVisible(false);
      }
    })();

    return () => {
      cancelado = true;
      if (blobUrlCreado) URL.revokeObjectURL(blobUrlCreado);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // listener de touch (passive:false)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleTouchStart = (e) => iniciarTrazo(e);
    const handleTouchMove = (e) => continuarTrazo(e);
    const handleTouchEnd = () => terminarTrazo();

    canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    canvas.addEventListener('touchend', handleTouchEnd);

    return () => {
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="firma-contenedor">
      <div className="cabecera-sign">
        <Button
          type="button"
          icon="pi pi-refresh"
          className="btn-outline-azul"
          onClick={limpiar}
          disabled={!tieneTrazo}
        />
        <label className="form-label mb-0">{label}</label>
      </div>

      <div
        style={{
          border: error ? '1px solid #e24c4c' : '1px solid #ddd',
          borderRadius: 8,
          width: '100%',
          maxWidth: CANVAS_WIDTH,
          touchAction: 'none',
          background: '#fff',
        }}
      >
        <canvas
          ref={canvasRef}
          width={CANVAS_WIDTH}
          height={CANVAS_HEIGHT}
          style={{ width: '100%', height: 'auto', display: 'block', cursor: 'crosshair' }}
          onMouseDown={iniciarTrazo}
          onMouseMove={continuarTrazo}
          onMouseUp={terminarTrazo}
          onMouseLeave={terminarTrazo}
        />
      </div>

      {error && <small className="text-danger d-block mt-1">{error}</small>}
      {!error && tieneTrazo && firmaGuardadaVisible && (
        <small className="text-muted d-block mt-1">Firma ya guardada — dibuja encima para reemplazarla.</small>
      )}
      {!error && !tieneTrazo && (
        <small className="text-muted d-block mt-1">Firma aquí con el dedo o el mouse.</small>
      )}
    </div>
  );
}

export default SignaturePad;